import { Label } from "@components/inputs/field/Label";
import { SwitchType } from "@components/inputs/switch/Switch";
import { TGetValueFn, TInfoValue } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrAbsenceCategoryEntity,
    PrAbsenceCategoryEntity,
    PrAbsenceCategoryOriginEntity,
    PrAbsenceCategoryTemporalCurrentEntity,
    PrAbsenceCategoryTemporalEntity,
    PrAbsenceCategoryTypeEntity,
    PrVacationEntitlementEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAbsenceCategoryOriginCode,
    PrAbsenceCategoryTypeCode,
    PrAbsenceCompensationCode,
    PrVacationEntitlementInclusionCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyFieldDefinition,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import i18next from "i18next";
import React from "react";

import { BasicInputSizes, FieldType, LabelStatus, Sort } from "../../../enums";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import AbsenceCategoryFormView from "./AbsenceCategoryFormView";

const AttributesLabelPath = BindingContext.localContext("attributesLabel");
const AttributesCheckboxGroupPath = BindingContext.localContext("attributesCheckboxGroup");
const attributesCheckboxFields = [
    PrAbsenceCategoryEntity.IsExcludedPeriod,
    PrAbsenceCategoryEntity.IsExcludedDay,
    PrAbsenceCategoryEntity.IsOnRegistry
];

const isOfType = (...validTypes: PrAbsenceCategoryOriginCode[]): TGetValueFn<boolean> => {
    return (args) => {
        const originCode = args.storage.getValueByPath(createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.OriginCode));
        return validTypes.includes(originCode);
    };
};

// Common formatter for Form and Table
const vacationEntitlementFormatter: TFormatterFn = (val, args) => {
    const entity = (args.entity ?? args.storage.data.entity) as IPrAbsenceCategoryEntity;
    if (entity.Type.VacationEntitlementInclusion) {
        return entity.Type.VacationEntitlementInclusion.Name;
    }
    return getEnumDisplayValue(EntityTypeName.PrVacationEntitlementInclusion, val as string);
};

const compensationTableFormatter: TFormatterFn = (val, args: IFormatOptions<IPrAbsenceCategoryEntity>) => {
    const { entity } = args;
    if (PrAbsenceCategoryOriginCode.Legislative === entity.Type?.OriginCode) {
        switch (entity.Type.CompensationCode) {
            case PrAbsenceCompensationCode.Paid:
                val = 100;
                break;
            case PrAbsenceCompensationCode.Unpaid:
                val = 0;
                break;
            case PrAbsenceCompensationCode.Special:
                return i18next.t("AbsenceCategory:Table.SpecialCompensation").toString();
        }
    }
    return `${val} ${args.unit}`;
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrAbsenceCategory}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
                createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin),
                PrAbsenceCategoryEntity.Description,
            ],
            filterDefinition: {
                [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name)]: {},
                [createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin)]: {
                    label: i18next.t("AbsenceCategory:Type"),
                    fieldSettings: {
                        displayName: PrAbsenceCategoryOriginEntity.Name
                    }
                },
                [PrAbsenceCategoryEntity.Description]: {},
                // [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate)]: {
                //     formatter: (val, args) => {
                //         return isDefined(val) ? `${val} %` : args.placeholder;
                //     }
                // }
            },
            isValueHelp: true
        }],
        initialSortBy: [{
            id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
            sort: Sort.Asc
        }],
        additionalProperties: [
            {
                id: PrAbsenceCategoryEntity.Type,
                additionalProperties: [
                    { id: PrAbsenceCategoryTypeEntity.CompensationCode },
                    { id: PrAbsenceCategoryTypeEntity.OriginCode },
                    { id: createPath(PrAbsenceCategoryTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            }
        ],
        columns: [
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
            createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin),
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate),
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion),
            PrAbsenceCategoryEntity.IsExcludedPeriod,
            PrAbsenceCategoryEntity.IsExcludedDay,
            PrAbsenceCategoryEntity.IsOnRegistry,
            PrAbsenceCategoryEntity.Description,
        ],
        columnDefinition: {
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name)]: {},
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate)]: {
                fieldSettings: {
                    unit: "%",
                },
                formatter: compensationTableFormatter,
                disableSort: true
            },
            [createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin)]: {
                label: i18next.t("AbsenceCategory:Type"),
                fieldSettings: {
                    displayName: PrAbsenceCategoryOriginEntity.Name
                }
            },
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion)]: {
                label: i18next.t("AbsenceCategory:Table.VacationEntitlementInclusion"),
                fieldSettings: {
                    displayName: PrVacationEntitlementEntity.Name
                },
                formatter: vacationEntitlementFormatter,
                disableSort: true
            },
            [PrAbsenceCategoryEntity.IsExcludedPeriod]: {
                label: i18next.t("AbsenceCategory:Form.IsExcludedPeriod")
            },
            [PrAbsenceCategoryEntity.IsExcludedDay]: {
                label: i18next.t("AbsenceCategory:Form.IsExcludedDay")
            },
            [PrAbsenceCategoryEntity.IsOnRegistry]: {
                label: i18next.t("AbsenceCategory:Form.IsOnRegistry")
            },
            [PrAbsenceCategoryEntity.Description]: {}
        },
        title: i18next.t("AbsenceCategory:Title"),
        tabsSettings: {}
    };
    const summary: ISummaryItem[] = [];
    const getTemporalDialogSettings = (fieldId: PrAbsenceCategoryTemporalCurrentEntity) => ({
        dialogTitle: i18next.t("AbsenceCategory:Form.PlanAbsenceCategoryTitle"),
        granularity: "month" as const,
        columns: [
            fieldId
        ]
    });

    const extraFieldContentAfter: TInfoValue<React.ReactElement> = (args) => {
        const openingButtonRenderer = getTemporalPropertyExtraFieldContentAfter();
        if (isOfType(PrAbsenceCategoryOriginCode.Legislative, PrAbsenceCategoryOriginCode.System)(args)){
            return null;
        }
        return openingButtonRenderer(args);
    };


    const form: IFormDef = {
        id: `${EntityTypeName.PrAbsenceCategory}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, i18next.t("AbsenceCategory:New"), storage.data.entity?.CurrentTemporalPropertyBag?.Name),
        isDeletable: true,
        formControl: AbsenceCategoryFormView,
        summary,
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrAbsenceCategoryTemporalEntity, PrAbsenceCategoryEntity),
            {
                id: PrAbsenceCategoryEntity.Type,
                additionalProperties: [
                    { id: PrAbsenceCategoryTypeEntity.OriginCode },
                    { id: createPath(PrAbsenceCategoryTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) },
                    { id: createPath(PrAbsenceCategoryTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            }
        ],
        fieldDefinition: {
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalEntity.Name,
                definition: {
                    width: BasicInputSizes.M,
                    isReadOnly: isOfType(PrAbsenceCategoryOriginCode.Legislative, PrAbsenceCategoryOriginCode.System),
                    fieldSettings: {
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.Name),
                    }
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalCurrentEntity.CompensationRate,
                definition: {
                    width: BasicInputSizes.S,
                    isVisible: isOfType(PrAbsenceCategoryOriginCode.Custom, PrAbsenceCategoryOriginCode.System),
                    isReadOnly: isOfType(PrAbsenceCategoryOriginCode.Legislative, PrAbsenceCategoryOriginCode.System),
                    isRequired: isOfType(PrAbsenceCategoryOriginCode.Custom),
                    fieldSettings: {
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.CompensationRate),
                        unit: "%"
                    },
                },
                currentDefinition: {
                    extraFieldContentAfter,
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.XL,
                    isReadOnly: isOfType(PrAbsenceCategoryOriginCode.Legislative, PrAbsenceCategoryOriginCode.System),
                    isRequired: isOfType(PrAbsenceCategoryOriginCode.Custom),
                    defaultValue: PrVacationEntitlementInclusionCode.Full,
                    fieldSettings: {
                        displayName: PrVacationEntitlementEntity.Name,
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion),
                    },
                    formatter: vacationEntitlementFormatter 
                },
                currentDefinition: {
                    extraFieldContentAfter,
                }
        
            }),
            [createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin)]: {
                type: FieldType.ComboBox,
                label: i18next.t("AbsenceCategory:Type"),
                defaultValue: PrAbsenceCategoryOriginCode.Custom,
                isReadOnly: true,
                fieldSettings: {
                    displayName: PrAbsenceCategoryOriginEntity.Name
                }
            },
            [PrAbsenceCategoryEntity.TypeCode]: {
                defaultValue: PrAbsenceCategoryTypeCode.Custom
            },
            [PrAbsenceCategoryEntity.Description]: {
                width: BasicInputSizes.XL
            },
            [AttributesLabelPath]: {
                type: FieldType.Custom,
                render: () => <Label>{i18next.t("AbsenceCategory:Form.AttributesGroup")}</Label>,
                customizationData: {
                    useForCustomization: false
                }
            },
            [AttributesCheckboxGroupPath]: {
                type: FieldType.CheckboxGroup,
                labelStatus: LabelStatus.Removed,
                width: "100%",
                isReadOnly: isOfType(PrAbsenceCategoryOriginCode.Legislative, PrAbsenceCategoryOriginCode.System),
                fieldSettings: {
                    items: attributesCheckboxFields.map(id => ({
                        id,
                        label: i18next.t(`AbsenceCategory:Form.${id}`)
                    }))
                },
                additionalProperties: attributesCheckboxFields.map(fieldId => ({ id: `/${fieldId}` }))
            },
            [PrAbsenceCategoryEntity.IsActive]: {
                type: FieldType.Switch,
                isDisabled: isOfType(PrAbsenceCategoryOriginCode.Legislative),
                fieldSettings: {
                    type: SwitchType.Icons
                },
                defaultValue: true
            },
            [PrAbsenceCategoryEntity.Color]: {
                type: FieldType.ColorPicker,
                width: BasicInputSizes.fitContent,
                defaultValue: "C_CHART_blue",
                fieldSettings: {
                    colors: [
                        "C_CHART_blue",
                        "C_CHART_green",
                        "C_CHART_yellow",
                        "C_CHART_red",
                        "C_CHART_orange",
                        "C_CHART_grey"
                    ]
                }
            },
            ...TemporalValidityFieldsDef
        },
        groups: [
            {
                id: "main",
                rows: [
                    [
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name) },
                        { id: createPath(PrAbsenceCategoryEntity.Type, PrAbsenceCategoryTypeEntity.Origin) },
                        { id: PrAbsenceCategoryEntity.Color },
                        { id: PrAbsenceCategoryEntity.Description },
                    ]
                ]
            },
            {
                id: "detail",
                title: i18next.t("AbsenceCategory:Form.DetailGroup"),
                rows: [
                    [
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate) },
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion) }
                    ]
                ]
            },
            {
                id: "attributes",
                rows: [
                    [{ id: AttributesLabelPath }],
                    [{ id: AttributesCheckboxGroupPath }]
                ]
            },
            {
                id: "status",
                title: i18next.t("AbsenceCategory:Form.StatusGroup"),
                rows: [
                    [
                        { id: PrAbsenceCategoryEntity.IsActive },
                    ]
                ]
            }
        ]
    };
    return {
        entitySet: EntitySetName.PrAbsenceCategories,
        table,
        form
    };
};

getDefinitions.translationFiles = ["AbsenceCategory", "Common"];
setDefByEntityType(EntityTypeName.PrAbsenceCategory, getDefinitions);